import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "reactstrap";
import notifications from "../../common/Components/Notifications/Notification";

const formatScore = (value?: number) => {
  if (value == null || Number.isNaN(value)) {
    return "";
  }
  return `${Math.round(value * 100)}%`;
};

const attrLabel = (item: any) => {
  if (!item) {
    return "";
  }
  const score = formatScore(item.confidence);
  return score ? `${item.value} · ${score}` : item.value;
};

const ProductAiSuggestions = (props: any) => {
  const productId = props.productId;
  const [enrichment, setEnrichment] = useState<any>(props.aiEnrichment || null);
  const [status, setStatus] = useState<"idle" | "loading" | "empty" | "error" | "ready">(
    props.aiEnrichment?.status === "DONE" ? "ready" : "idle"
  );
  const [errorMessage, setErrorMessage] = useState("");
  const [similar, setSimilar] = useState<any[]>([]);
  const [similarStatus, setSimilarStatus] = useState<"idle" | "loading" | "empty" | "error" | "ready">("idle");

  useEffect(() => {
    if (props.aiEnrichment?.status === "DONE") {
      setEnrichment(props.aiEnrichment);
      setStatus("ready");
    } else if (props.aiEnrichment?.status === "FAILED") {
      setEnrichment(props.aiEnrichment);
      setStatus("error");
      setErrorMessage(props.aiEnrichment?.errorMessage || "Photo analysis failed");
    }
  }, [props.aiEnrichment]);

  const notify = (response: any, fallback: string) => {
    if (response?.success) {
      notifications({
        title: "Success!",
        message: response?.message || "Saved",
        type: "success",
      });
      return true;
    }
    notifications({
      title: "Notice",
      message: response?.message || fallback,
      type: "danger",
    });
    return false;
  };

  const runEnrich = () => {
    if (!productId || !props.enrichProductAi) {
      return;
    }
    setStatus("loading");
    setErrorMessage("");
    props.enrichProductAi(productId, (response: any) => {
      if (response?.success && response?.data) {
        setEnrichment(response.data);
        setStatus(response.data?.status === "DONE" ? "ready" : "empty");
        notify(response, "Suggestions ready");
        if (props.onApplied) {
          props.onApplied();
        }
        return;
      }
      setStatus("error");
      setErrorMessage(response?.message || "Unable to analyse product photo");
      notify(response, "Unable to analyse product photo");
    });
  };

  const accept = (payload: any) => {
    if (!productId || !props.acceptProductAi) {
      return;
    }
    setStatus("loading");
    props.acceptProductAi(productId, payload, (response: any) => {
      if (response?.success) {
        setEnrichment(response.data || enrichment);
        setStatus("ready");
        notify(response, "Applied suggestions");
        if (props.onApplied) {
          props.onApplied();
        }
        return;
      }
      setStatus("ready");
      notify(response, "Unable to apply suggestions");
    });
  };

  const dismiss = () => {
    if (!productId || !props.rejectProductAi) {
      return;
    }
    props.rejectProductAi(productId, (response: any) => {
      if (response?.success) {
        setEnrichment(response.data || enrichment);
        notify(response, "Dismissed");
        return;
      }
      notify(response, "Unable to dismiss suggestions");
    });
  };

  const loadSimilar = () => {
    if (!productId || !props.getSimilarProducts) {
      return;
    }
    setSimilarStatus("loading");
    props.getSimilarProducts(productId, (response: any) => {
      if (response?.success) {
        const docs = Array.isArray(response?.data?.docs)
          ? response.data.docs
          : Array.isArray(response?.data)
          ? response.data
          : [];
        setSimilar(docs);
        setSimilarStatus(docs.length ? "ready" : "empty");
        return;
      }
      setSimilarStatus("error");
      setErrorMessage(response?.message || "Could not load similar products");
    });
  };

  const colors = enrichment?.attributes?.colors || [];
  const types = enrichment?.attributes?.productTypes || [];
  const categories = enrichment?.attributes?.categories || [];
  const tags = enrichment?.suggestedTags || [];
  const dismissed = enrichment?.reviewStatus === "DISMISSED";
  const hasSuggestions = Boolean(colors[0] || types[0] || categories[0] || tags.length);

  if (!productId) {
    return null;
  }

  return (
    <div className="product-ai-suggestions">
      <div className="product-ai-suggestions__toolbar">
        <div className="product-ai-suggestions__title">
          <strong>Photo AI</strong>
          <p>
            Analyse the product photo to store AI generated tags and a full
            embedding for search and catalogues. Tags stay as suggestions until
            you accept them onto the product.
          </p>
        </div>
        <div className="product-ai-suggestions__header-actions">
          <Button
            type="button"
            className="ResetBtn"
            disabled={status === "loading"}
            onClick={runEnrich}
          >
            {status === "loading" ? "Analysing…" : "Analyse photo"}
          </Button>
          <Button
            type="button"
            className="ResetBtn"
            disabled={similarStatus === "loading"}
            onClick={loadSimilar}
          >
            {similarStatus === "loading" ? "Finding…" : "Find similar"}
          </Button>
        </div>
      </div>
      <p className="product-ai-suggestions__embed">
        {enrichment?.embeddingStored
          ? "Embedding stored — used for Find similar, product search, and catalogue add-from-inventory."
          : "Run Analyse photo to generate AI tags and store the embedding."}
      </p>
      {status === "loading" ? <p className="product-ai-suggestions__state">Analysing photo…</p> : null}
      {status === "error" ? (
        <p className="product-ai-suggestions__state product-ai-suggestions__state--error">
          {errorMessage || "Unable to analyse product photo"}
        </p>
      ) : null}
      {status === "empty" ? (
        <p className="product-ai-suggestions__state">No confident suggestions from this photo.</p>
      ) : null}
      {status === "ready" && enrichment ? (
        <>
          {dismissed ? (
            <p className="product-ai-suggestions__state">Suggestions dismissed. You can analyse again.</p>
          ) : null}
          <div className="product-ai-suggestions__grid">
            <section>
              <span>Color</span>
              <div className="product-ai-suggestions__chips">
                {colors.length ? (
                  colors.map((item: any, i: number) => (
                    <em key={`color-${i}-${item?.value}`}>{attrLabel(item)}</em>
                  ))
                ) : (
                  <em className="is-empty">—</em>
                )}
              </div>
              <Button
                type="button"
                className="ResetBtn"
                disabled={!colors[0]}
                onClick={() => accept({ applyColor: true, colorValue: colors[0]?.value })}
              >
                Accept color
              </Button>
            </section>
            <section>
              <span>Type</span>
              <div className="product-ai-suggestions__chips">
                {types.length ? (
                  types.map((item: any, i: number) => (
                    <em key={`type-${i}-${item?.value}`}>{attrLabel(item)}</em>
                  ))
                ) : (
                  <em className="is-empty">—</em>
                )}
              </div>
              <Button
                type="button"
                className="ResetBtn"
                disabled={!types[0]}
                onClick={() => accept({ applyType: true, typeValue: types[0]?.value })}
              >
                Accept type
              </Button>
            </section>
            <section>
              <span>Category</span>
              <div className="product-ai-suggestions__chips">
                {categories.length ? (
                  categories.map((item: any, i: number) => (
                    <em key={`category-${i}-${item?.value}`}>{attrLabel(item)}</em>
                  ))
                ) : (
                  <em className="is-empty">—</em>
                )}
              </div>
              <Button
                type="button"
                className="ResetBtn"
                disabled={!categories[0]}
                onClick={() => accept({ applyCategory: true, categoryValue: categories[0]?.value })}
              >
                Accept category
              </Button>
            </section>
            <section>
              <span>AI generated tags</span>
              <div className="product-ai-suggestions__chips">
                {tags.length ? (
                  tags.map((tag: string, i: number) => (
                    <em key={`tag-${i}-${tag}`}>{tag}</em>
                  ))
                ) : (
                  <em className="is-empty">—</em>
                )}
              </div>
              <Button
                type="button"
                className="ResetBtn"
                disabled={!tags.length}
                onClick={() => accept({ applyTags: true })}
              >
                Accept tags
              </Button>
            </section>
          </div>
          <div className="product-ai-suggestions__actions">
            <Button
              type="button"
              className="ResetBtn"
              disabled={!hasSuggestions}
              onClick={() =>
                accept({
                  applyColor: Boolean(colors[0]),
                  applyTags: tags.length > 0,
                  applyType: Boolean(types[0]),
                  applyCategory: Boolean(categories[0]),
                })
              }
            >
              Accept all
            </Button>
            <Button type="button" className="ResetBtn" onClick={dismiss}>
              Dismiss
            </Button>
          </div>
        </>
      ) : null}
      {similarStatus === "empty" ? (
        <p className="product-ai-suggestions__state">
          No similar products with embeddings yet. Analyse this photo first if an embedding has not
          been stored.
        </p>
      ) : null}
      {similarStatus === "error" ? (
        <p className="product-ai-suggestions__state product-ai-suggestions__state--error">
          Could not load similar products
          {errorMessage ? `: ${errorMessage}` : ". Analyse the photo first if no embedding is stored."}
        </p>
      ) : null}
      {similarStatus === "ready" ? (
        <ul className="product-ai-suggestions__similar">
          {similar.map((item: any) => {
            const id = item?._id || item?.id;
            const label = `${item?.name || item?.productUniqueId || "Product"}${
              item?.color ? ` · ${item.color}` : ""
            }`;
            return (
              <li key={id || item?.productUniqueId}>
                {id ? <Link to={`/edit-product/${id}`}>{label}</Link> : label}
              </li>
            );
          })}
        </ul>
      ) : null}
    </div>
  );
};

export default ProductAiSuggestions;
